/**
 * AboveChromeSlot — portal target rendered ABOVE the AppShell header band
 * (just below the ImpersonationBanner). For thin, dismissible, page-owned
 * announcement strips (sample-data, plan-limit, etc.) that should sit above
 * the canon chrome (PageHeader + cards + panels) without competing with it.
 *
 * Mirrors PageHeaderSlot's pattern: AppShell mounts a stable DOM node +
 * publishes it via context; pages render their banner via <AboveChromeSlot>
 * children, which portal into that node.
 *
 * Batch 9 — Dashboard canon strip-down. The Dashboard's <SampleDataBanner>
 * is the first consumer; the slot stays empty (height 0) on pages that
 * don't render into it.
 *
 * No emojis. Frontend-only.
 */
import { createContext, useContext } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

interface AboveChromeSlotContextValue {
  // DOM node into which children are portaled. Null until AppShell mounts.
  target: HTMLElement | null;
}

const AboveChromeSlotContext = createContext<AboveChromeSlotContextValue>({
  target: null,
});

export function AboveChromeSlotProvider({
  target,
  children,
}: {
  target: HTMLElement | null;
  children: ReactNode;
}): JSX.Element {
  return (
    <AboveChromeSlotContext.Provider value={{ target }}>
      {children}
    </AboveChromeSlotContext.Provider>
  );
}

export function AboveChromeSlot({
  children,
}: {
  children: ReactNode;
}): JSX.Element | null {
  const { target } = useContext(AboveChromeSlotContext);
  if (!target) return null;
  return createPortal(children, target);
}
